import { SENSOR_TYPES, CALM_WIND_THRESHOLD_MS, toEasternPlotDate } from './sensorConfig';

// --- Circular math helpers ---
export const mod360 = (d) => ((d % 360) + 360) % 360;
const circDist = (a, b) => { const diff = Math.abs(mod360(a) - mod360(b)); return Math.min(diff, 360 - diff); };

export const circularMedianDeg = (anglesDeg = []) => {
  const vals = anglesDeg.map(Number).filter(Number.isFinite);
  if (!vals.length) return null;
  let best = vals[0], bestCost = Infinity;
  for (const cand of vals) {
    const cost = vals.reduce((s, v) => s + circDist(cand, v), 0);
    if (cost < bestCost) { bestCost = cost; best = cand; }
  }
  return mod360(best);
};

export const median = (nums = []) => {
  const arr = nums.map(Number).filter(Number.isFinite).sort((a, b) => a - b);
  if (!arr.length) return null;
  const m = Math.floor(arr.length / 2);
  return arr.length % 2 ? arr[m] : (arr[m - 1] + arr[m]) / 2;
};

export const degToCompass = (degFrom) => {
  const dirs = ['N','NNE','NE','ENE','E','ESE','SE','SSE','S','SSW','SW','WSW','W','WNW','NW','NNW'];
  return dirs[Math.round(mod360(degFrom) / 22.5) % 16];
};

export const mpsToMph = (m) => (Number.isFinite(m) ? m * 2.23693629 : m);

export const computeArrowResolution = (msRange) => {
  const oneDay = 86400000;
  if (msRange < oneDay) return 15;
  if (msRange < 3 * oneDay) return 30;
  return 60;
};

// --- Direction rose ---
export const buildDirectionRose = (transformedData, { dirKind, weightKind = null, nBins = 16 }) => {
  const dirs = transformedData[dirKind] || [];
  const mags = weightKind ? (transformedData[weightKind] || []) : null;
  if (!dirs.length) return { theta: [], r: [] };

  let magMap = null;
  if (mags?.length) magMap = new Map(mags.map(m => [new Date(m.time).getTime(), Number(m.sensor_value)]));

  const binSize = 360 / nBins;
  const bins = new Array(nBins).fill(0);
  for (const d of dirs) {
    const val = Number(d.sensor_value);
    if (!Number.isFinite(val)) continue;
    const idx = Math.min(nBins - 1, Math.floor(((val % 360) + 360) % 360 / binSize));
    const t = new Date(d.time).getTime();
    const w = (magMap && Number.isFinite(magMap.get(t))) ? Math.max(0, magMap.get(t)) : 1;
    bins[idx] += w;
  }
  return { theta: bins.map((_, i) => i * binSize + binSize / 2), r: bins };
};

// --- Compass rose overlay for Plotly ---
export const buildCompassRose = (corner = 'tr') => {
  const padX = 0.035, padY = 0.04;
  const cx = corner === 'tr' ? 1 - padX : padX;
  const cy = 1 - padY;
  const r = 0.055;
  const shapes = [
    { type: 'circle', xref: 'paper', yref: 'paper', x0: cx - r, x1: cx + r, y0: cy - r, y1: cy + r, line: { color: 'rgba(0,0,0,0.45)', width: 1 }, fillcolor: 'rgba(255,255,255,0.65)', layer: 'above' },
    { type: 'line', xref: 'paper', yref: 'paper', x0: cx, x1: cx, y0: cy - r, y1: cy + r, line: { color: 'rgba(0,0,0,0.35)', width: 1 } },
    { type: 'line', xref: 'paper', yref: 'paper', x0: cx - r, x1: cx + r, y0: cy, y1: cy, line: { color: 'rgba(0,0,0,0.35)', width: 1 } },
  ];
  const ann = (x, y, text, xa = 'center', ya = 'middle') => ({
    xref: 'paper', yref: 'paper', x, y, text, showarrow: false,
    xanchor: xa, yanchor: ya, font: { size: 11, color: '#333' }, bgcolor: 'rgba(0,0,0,0)',
  });
  const d = r + 0.018;
  return {
    shapes,
    annotations: [
      ann(cx, cy + d, 'N', 'center', 'bottom'), ann(cx + d, cy, 'E', 'left', 'middle'),
      ann(cx, cy - d, 'S', 'center', 'top'),    ann(cx - d, cy, 'W', 'right', 'middle'),
    ],
  };
};

// --- Arrow-baseline wind/wave time series ---
export const buildArrowBaselineTimeSeries = (
  transformedData, dirKind, magKind,
  { dirAliases = [], resolutionMin = 60, applyCalm = false, calmThreshold = CALM_WIND_THRESHOLD_MS, arrowCap = 48, calmDot = { size: 5 } } = {}
) => {
  const empty = { traces: [], annotations: [], meanAnnots: [], meanShapes: [], baselineShape: null };

  let dirs = transformedData[dirKind] || [];
  if (!dirs.length) {
    for (const alt of dirAliases) { if ((transformedData[alt] || []).length) { dirs = transformedData[alt]; break; } }
  }
  const mags = transformedData[magKind] || [];
  if (!dirs.length) return empty;

  const bucketMs = resolutionMin * 60 * 1000;
  const floor = (t) => Math.floor(new Date(t).getTime() / bucketMs) * bucketMs;

  const dirByBucket = new Map();
  const magByBucket = new Map();

  for (const d of dirs) {
    const ts = floor(d.time);
    const toDeg = (Number(d.sensor_value) + 180) % 360;
    if (!Number.isFinite(toDeg)) continue;
    if (!dirByBucket.has(ts)) dirByBucket.set(ts, []);
    dirByBucket.get(ts).push(toDeg);
  }
  for (const m of mags) {
    const ts = floor(m.time);
    const v = Number(m.sensor_value);
    if (!Number.isFinite(v)) continue;
    if (!magByBucket.has(ts)) magByBucket.set(ts, []);
    magByBucket.get(ts).push(v);
  }

  const buckets = Array.from(dirByBucket.keys()).sort((a, b) => a - b);
  const xs = [], dirsTo = [], magsAvg = [];
  for (const b of buckets) {
    let sx = 0, sy = 0;
    for (const deg of dirByBucket.get(b)) { const r = deg * Math.PI / 180; sx += Math.cos(r); sy += Math.sin(r); }
    const meanDeg = ((Math.atan2(sy, sx) * 180 / Math.PI) + 360) % 360;
    const mList = magByBucket.get(b) || [];
    xs.push(toEasternPlotDate(b));
    dirsTo.push(meanDeg);
    magsAvg.push(mList.length ? mList.reduce((a, c) => a + c, 0) / mList.length : NaN);
  }
  if (!xs.length) return empty;

  const valids = magsAvg.filter(Number.isFinite);
  const p90 = valids.length ? [...valids].sort((a, b) => a - b)[Math.floor(0.9 * (valids.length - 1))] : 1;
  const scaleMax = Math.max(p90, 1e-6);
  const pxLen = (m) => !Number.isFinite(m) ? 18 : 18 + 10 * Math.cbrt(Math.max(0, Math.min(m, scaleMax)) / scaleMax);

  const annotations = [];
  const calmXs = [], calmHover = [], headXs = [], headYs = [], headHover = [];
  const stride = Math.max(1, Math.ceil(xs.length / arrowCap));

  for (let i = 0; i < xs.length; i += stride) {
    const x = xs[i], m = magsAvg[i], dir = dirsTo[i];
    const dispMag = Number.isFinite(m) ? (magKind === 'wave_height' ? (m * 100).toFixed(1) : m.toFixed(2)) : '—';
    const dispUnit = magKind === 'wave_height' ? 'cm' : (SENSOR_TYPES[magKind]?.unit || '');

    if (applyCalm && (!Number.isFinite(m) || m < calmThreshold)) {
      calmXs.push(x);
      calmHover.push(`${SENSOR_TYPES[dirKind]?.name}: calm<br>${SENSOR_TYPES[magKind]?.name}: ${dispMag} ${dispUnit}`);
      continue;
    }
    const theta = dir * Math.PI / 180;
    const L = pxLen(m);
    annotations.push({
      x, xref: 'x', y: 1, yref: 'y',
      axref: 'pixel', ayref: 'pixel',
      // Pixel offsets are screen coords (+y is DOWN), so a compass bearing
      // measured clockwise from north maps to (sin, -cos) — not (sin, cos),
      // which mirrors north and south while leaving east/west looking right.
      ax: Math.sin(theta) * L, ay: -Math.cos(theta) * L,
      showarrow: true, arrowhead: 2, arrowsize: 1.4, arrowwidth: 2, opacity: 0.95,
      arrowcolor: SENSOR_TYPES[dirKind]?.color || '#444',
      arrowside: 'start', startarrowhead: 2,
    });
    headXs.push(x); headYs.push(1);
    headHover.push(`${SENSOR_TYPES[dirKind]?.name}: ${dir.toFixed(0)}° (TO)<br>${SENSOR_TYPES[magKind]?.name}: ${dispMag} ${dispUnit}`);
  }

  return {
    traces: [
      { x: xs, y: Array(xs.length).fill(1), type: 'scatter', mode: 'lines', line: { width: 0 }, hoverinfo: 'skip', showlegend: false, opacity: 0 },
      calmXs.length ? { x: calmXs, y: Array(calmXs.length).fill(1), type: 'scatter', mode: 'markers', marker: { symbol: 'circle', size: calmDot.size, color: 'rgba(0,0,0,0.45)' }, hovertemplate: '%{text}<extra></extra>', text: calmHover, showlegend: false } : null,
      headXs.length ? { x: headXs, y: headYs, type: 'scatter', mode: 'markers', marker: { size: 14, opacity: 0.001 }, hovertemplate: '%{text}<extra></extra>', text: headHover, showlegend: false } : null,
    ].filter(Boolean),
    annotations,
    meanAnnots: [], meanShapes: [],
    baselineShape: { type: 'line', xref: 'x', yref: 'y', x0: xs[0], x1: xs[xs.length - 1], y0: 1, y1: 1, line: { width: 0.8, color: 'rgba(0,0,0,0.25)', dash: 'dot' }, layer: 'below' },
  };
};
